import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

interface AgentToggleOptions {
  // Set by direct user actions (FAB click, iframe close button). Ambient
  // toggles — flag flips, breakpoint changes, navigate-away — leave it false so
  // consumers can persist a preference only when the user meant it.
  intentional?: boolean
  // Text to seed the composer with on open (e.g. the command-palette query).
  // The widget posts it into the iframe once open, then consumes it.
  prompt?: string
}

export const useOwnerAgentStore = defineStore('ownerAgent', () => {
  const isOpen = ref(false)

  // Monotonic counter bumped only on an intentional toggle. Consumers watch it
  // to react to user intent without inferring it from `isOpen` transitions.
  const intentionalToggleCount = ref(0)

  const pendingPrompt = ref<string | null>(null)
  const embeddedHestiaOverlays = ref(new Set<object>())
  const hasEmbeddedHestiaOverlay = computed(
    () => embeddedHestiaOverlays.value.size > 0
  )

  const setEmbeddedHestiaOverlay = (source: object, isOpen: boolean): void => {
    if (isOpen) embeddedHestiaOverlays.value.add(source)
    else embeddedHestiaOverlays.value.delete(source)
  }

  const open = ({
    intentional = false,
    prompt,
  }: AgentToggleOptions = {}): void => {
    if (prompt !== undefined) pendingPrompt.value = prompt
    isOpen.value = true
    if (intentional) intentionalToggleCount.value += 1
  }

  const close = ({ intentional = false }: AgentToggleOptions = {}): void => {
    isOpen.value = false
    if (intentional) intentionalToggleCount.value += 1
  }

  // Read-and-clear the seeded prompt; the widget calls this after it has posted
  // the prompt into the iframe so a later open doesn't re-seed stale text.
  const consumePendingPrompt = (): string | null => {
    const prompt = pendingPrompt.value
    pendingPrompt.value = null
    return prompt
  }

  return {
    close,
    consumePendingPrompt,
    hasEmbeddedHestiaOverlay,
    intentionalToggleCount: readonly(intentionalToggleCount),
    isOpen,
    open,
    pendingPrompt: readonly(pendingPrompt),
    setEmbeddedHestiaOverlay,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOwnerAgentStore, import.meta.hot))
}
